import { toNumber } from './dioptre.js';
import { STOCK_ADJUSTMENT_REASONS, } from './enums.js';
import { add, money, zero } from './money.js';
/**
 * Stock movements, valuation and stocktake. §6.10 `INV-001` to `INV-012`.
 *
 * **Which costing method. Settled: weighted average, and `INV-002` was amended.** This module was
 * written while the PRD said two things. `INV-002` was a `MUST` that the method is "configurable:
 * weighted average or FIFO"; `P-05` in §19.1 says "Weighted average costing ... FIFO buys precision
 * nobody at this scale asks for", and Appendix Z's gross margin report names "weighted average
 * cost". `decisions/0009` resolved it in `P-05`'s favour and the PRD is at v1.3 with `INV-002`
 * amended, so **FIFO is out of v1**.
 *
 * Both methods are still implemented here and the method is still a required argument on every
 * call. That is deliberate rather than leftover: the argument is what stops a code path falling
 * back to a method silently, and it now has one legal value in v1. Removing the FIFO path would
 * save nothing and would have to be rebuilt the day the decision is revisited.
 *
 * **One representation for both.** A position is a list of cost layers. FIFO keeps one layer per
 * receipt and consumes from the front. Weighted average keeps at most one layer, merged on every
 * receipt, so its average cost is value divided by quantity and is never stored as a fraction.
 * Issues use the same consume routine in both methods.
 *
 * **Rounding.** Appendix D `MR-04` allows rounding at three points (line, order, cash tender) and a
 * stock issue is none of them. A weighted average cost is a fraction of a minor unit, so something
 * has to give. This module rounds nowhere: it apportions the pool exactly. An issue of `q` of `n`
 * units takes `value * q / n` with integer truncation, and the truncated remainder stays in the
 * pool, so the last unit out takes whatever is left. Across any sequence of issues, cost out plus
 * value remaining equals value in, to the minor unit. That is a gap in Appendix D, not a fourth
 * rounding point, and it is reported as one.
 *
 * Money never leaves `bigint`. Quantities are whole units: a contact lens box, a frame, a blank.
 */
export class StockError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'StockError';
    }
}
/** `INV-002`, `P-05`: the method the decision register names. Never applied implicitly. */
export const NAMED_COSTING_METHOD = 'weighted_average';
export function openPosition(ownership, currency) {
    zero(currency);
    return { ownership, quantity: 0, layers: [], currency };
}
export function positionValue(p) {
    return money(p.layers.reduce((sum, l) => sum + l.value.minor, 0n), p.currency);
}
// ---------------------------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------------------------
function assertQuantity(q, what) {
    if (!Number.isSafeInteger(q) || q <= 0) {
        throw new StockError('INVALID_QUANTITY', `${what} must be a positive whole number, got ${q}`);
    }
}
function assertCost(p, cost, what) {
    if (cost.currency !== p.currency) {
        throw new StockError('CURRENCY_MISMATCH', `${what} is ${cost.currency}, position is ${p.currency}`);
    }
    if (cost.minor < 0n)
        throw new StockError('NEGATIVE_COST', `${what} cannot be negative`);
}
function assertThreshold(v, what) {
    if (v === undefined)
        return;
    if (!Number.isSafeInteger(v) || v < 0) {
        throw new StockError('INVALID_THRESHOLD', `${what} must be a whole number of zero or more`);
    }
}
/**
 * The invariant every function here relies on and nothing used to state: the cost layers account
 * for exactly the stock on hand.
 *
 * A position is not only built by this module. It is read off a row, so an inconsistent one is
 * reachable input, and the arithmetic treats `quantity` and the layers as two views of one fact.
 * A position of 2 units carrying a 5-unit layer used to lose the other three units' value on the
 * next issue, silently, because the issue apportions by the layer's own quantity and then drops
 * what is left when the position empties. Refusing it is the only answer that keeps
 * "cost out plus value remaining equals value in" true.
 *
 * Negative stock carries no layers: the units are already gone and their cost went with them
 * (§12 case 47). Consignment carries none either, which is what makes `INV-003` structural rather
 * than merely intended.
 */
function assertConsistent(p, what = 'Position') {
    if (!Number.isSafeInteger(p.quantity)) {
        throw new StockError('POSITION_INCONSISTENT', `${what} quantity ${p.quantity} is not a whole number`);
    }
    let layered = 0;
    for (const l of p.layers) {
        if (!Number.isSafeInteger(l.quantity) || l.quantity <= 0) {
            throw new StockError('POSITION_INCONSISTENT', `${what} has a cost layer of ${l.quantity} units; a layer holds at least one`);
        }
        if (l.value.currency !== p.currency) {
            throw new StockError('CURRENCY_MISMATCH', `${what} is ${p.currency} but a cost layer is ${l.value.currency}`);
        }
        layered += l.quantity;
    }
    if (p.ownership !== 'owned') {
        if (layered > 0) {
            throw new StockError('POSITION_INCONSISTENT', `${what} is ${p.ownership} and cannot hold cost layers (INV-003)`);
        }
        return;
    }
    const onHand = Math.max(p.quantity, 0);
    if (layered !== onHand) {
        throw new StockError('POSITION_INCONSISTENT', `${what} holds ${onHand} units but its cost layers account for ${layered}`);
    }
}
/** `value * part / whole`, truncating. The one place a fraction of a minor unit is resolved. */
function portion(value, part, whole) {
    return (value * BigInt(part)) / BigInt(whole);
}
function assertMethodFits(p, method) {
    if (method === 'weighted_average' && p.layers.length > 1) {
        throw new StockError('COSTING_METHOD_MISMATCH', 'Position holds several cost layers but the tenant method is weighted average. ' +
            'The method changes only through changeCostingMethod, the INV-002 revaluation event.');
    }
}
function requireKnownOwnership(p) {
    if (p.ownership === 'on_loan') {
        throw new StockError('OWNERSHIP_UNSPECIFIED', 'Appendix E lists on_loan but no requirement says how it is valued, sold or settled');
    }
}
/** Takes `qty` units off the front of the layers. `qty` must not exceed what the layers hold. */
function takeLayers(layers, qty, currency) {
    let need = qty;
    let value = 0n;
    const rest = [];
    for (const layer of layers) {
        if (need === 0) {
            rest.push(layer);
        }
        else if (layer.quantity <= need) {
            value += layer.value.minor;
            need -= layer.quantity;
        }
        else {
            const part = portion(layer.value.minor, need, layer.quantity);
            value += part;
            rest.push({
                quantity: layer.quantity - need,
                value: money(layer.value.minor - part, currency),
            });
            need = 0;
        }
    }
    return { value, rest };
}
function mergeLayers(layers, currency) {
    if (layers.length === 0)
        return [];
    const quantity = layers.reduce((s, l) => s + l.quantity, 0);
    const value = layers.reduce((s, l) => s + l.value.minor, 0n);
    return [{ quantity, value: money(value, currency) }];
}
/**
 * What a stock movement is. `INV-001`, `INV-004`, `INV-005`.
 *
 * **Not registered in Appendix E**, so it is a constant rather than a transcription. It lived in
 * `packages/db` beside the check constraint that reads it, and moved here when `apps/counter`
 * needed the same six words: the counter cannot import the schema package, and two lists of the
 * same kinds is how a screen comes to show a movement the database cannot hold. The adjustment
 * reason (`count_variance`, `damage`) is registered and is a separate column.
 */
export const STOCK_MOVEMENT_KINDS = [
    'receipt',
    'sale',
    'transfer_out',
    'transfer_in',
    'adjustment',
    'reversal',
];
/** Every flag a position can carry, as a list as well as a type, so a table can be proved whole. */
export const STOCK_FLAGS = ['negative_stock'];
/** Reasons that describe stock leaving without a sale. `revaluation` is value-only, not a quantity. */
const LOSS_REASONS = [
    'damage',
    'shrinkage',
    'expiry',
    'write_off',
    'in_house_breakage',
    'sample',
];
const COUNT_REASONS = ['count_variance', ...LOSS_REASONS];
function assertReason(reason, allowed) {
    if (!STOCK_ADJUSTMENT_REASONS.includes(reason)) {
        throw new StockError('UNKNOWN_REASON', `${String(reason)} is not an Appendix E stock adjustment reason`);
    }
    if (!allowed.includes(reason)) {
        throw new StockError('REASON_NOT_ALLOWED', `Reason ${reason} is not valid for this movement`);
    }
}
function outflow(p, qty, allowNegative) {
    if (!allowNegative && qty > p.quantity) {
        throw new StockError('INSUFFICIENT_STOCK', `Cannot remove ${qty}, only ${p.quantity} on hand`);
    }
    const available = Math.max(p.quantity, 0);
    const costed = Math.min(qty, available);
    const taken = takeLayers(p.layers, costed, p.currency);
    const quantity = p.quantity - qty;
    // No `quantity > 0` fallback: the layers account for exactly the stock on hand
    // (`assertConsistent`), so emptying the position consumes them and `rest` is already empty. The
    // fallback it replaces discarded whatever was left, which is how an inconsistent position used
    // to lose value instead of being refused.
    return {
        position: { ...p, quantity, layers: taken.rest },
        value: taken.value,
        uncosted: qty - costed,
    };
}
/**
 * Units that arrive while the position is negative first cover units already sold at no cost.
 * Their share of the receipt cost is returned as `trueUp`, to be booked to COGS in the current
 * period rather than into inventory, so the shop's value is never inflated by stock already gone.
 */
function inflow(p, qty, cost, method) {
    const covered = Math.min(qty, Math.max(-p.quantity, 0));
    const trueUp = covered > 0 ? portion(cost, covered, qty) : 0n;
    const remaining = qty - covered;
    const remainingValue = cost - trueUp;
    const quantity = p.quantity + qty;
    // A position that was negative held no layers, so there is nothing to reset here.
    let layers = p.layers;
    if (remaining > 0) {
        const layer = { quantity: remaining, value: money(remainingValue, p.currency) };
        layers = method === 'fifo' ? [...layers, layer] : mergeLayers([...layers, layer], p.currency);
    }
    return { position: { ...p, quantity, layers }, valueAdded: remainingValue, trueUp };
}
function result(position, quantityDelta, valueChange, extra = {}) {
    const flags = position.quantity < 0 ? ['negative_stock'] : [];
    return {
        position,
        quantityDelta,
        inventoryValueChange: money(valueChange, position.currency),
        flags,
        ...(extra.cost ? { cost: extra.cost } : {}),
        ...(extra.costTrueUp && extra.costTrueUp.minor !== 0n ? { costTrueUp: extra.costTrueUp } : {}),
        ...(extra.settlement ? { settlement: extra.settlement } : {}),
        ...(extra.shipment ? { shipment: extra.shipment } : {}),
        ...(extra.discrepancy ? { discrepancy: extra.discrepancy } : {}),
        ...(extra.uncostedQuantity ? { uncostedQuantity: extra.uncostedQuantity } : {}),
    };
}
// ---------------------------------------------------------------------------------------------
// Movements
// ---------------------------------------------------------------------------------------------
/**
 * Applies one movement to one position. Pure: returns the new position and what the ledger needs
 * (`ACC-001`: stock adjustment, write-off, COGS), never mutates.
 */
export function applyMovement(position, movement, method) {
    requireKnownOwnership(position);
    assertConsistent(position);
    assertMethodFits(position, method);
    const p = position;
    const owned = p.ownership === 'owned';
    const ccy = p.currency;
    switch (movement.kind) {
        case 'receipt': {
            assertQuantity(movement.quantity, 'Receipt quantity');
            assertCost(p, movement.cost, 'Receipt cost');
            if (!owned) {
                throw new StockError('WRONG_OWNERSHIP', 'A purchase receipt goes into owned stock; use consignment_take_up for memo stock');
            }
            const r = inflow(p, movement.quantity, movement.cost.minor, method);
            return result(r.position, movement.quantity, r.valueAdded, {
                costTrueUp: money(r.trueUp, ccy),
            });
        }
        case 'consignment_take_up': {
            assertQuantity(movement.quantity, 'Take-up quantity');
            if (p.ownership !== 'consignment') {
                throw new StockError('WRONG_OWNERSHIP', 'Consignment stock goes into a consignment position (INV-003)');
            }
            return result({ ...p, quantity: p.quantity + movement.quantity }, movement.quantity, 0n);
        }
        case 'sale': {
            assertQuantity(movement.quantity, 'Sale quantity');
            if (movement.expiry) {
                assertSellableByExpiry(movement.expiry.expiryDate, movement.expiry.today, movement.expiry.override);
            }
            // §12 case 47: a sale of stock counted wrong is allowed, flagged, never silent.
            const o = outflow(p, movement.quantity, true);
            if (!owned) {
                if (!movement.supplierCost) {
                    throw new StockError('SETTLEMENT_COST_REQUIRED', 'A consignment sale needs the amount owed to the supplier (INV-003)');
                }
                assertCost(p, movement.supplierCost, 'Supplier cost');
                return result(o.position, -movement.quantity, 0n, {
                    cost: movement.supplierCost,
                    settlement: { kind: 'due', amount: movement.supplierCost },
                });
            }
            return result(o.position, -movement.quantity, -o.value, {
                cost: money(o.value, ccy),
                uncostedQuantity: o.uncosted,
            });
        }
        case 'customer_return': {
            assertQuantity(movement.quantity, 'Return quantity');
            assertCost(p, movement.cost, 'Return cost');
            if (owned) {
                const r = inflow(p, movement.quantity, movement.cost.minor, method);
                return result(r.position, movement.quantity, r.valueAdded, {
                    costTrueUp: money(r.trueUp, ccy),
                });
            }
            if (typeof movement.remitted !== 'boolean') {
                throw new StockError('REMITTANCE_UNKNOWN', 'A consignment return must say whether the supplier was already paid (§15.6)');
            }
            return result({ ...p, quantity: p.quantity + movement.quantity }, movement.quantity, 0n, {
                settlement: movement.remitted
                    ? { kind: 'supplier_receivable', amount: movement.cost }
                    : { kind: 'reversed', amount: movement.cost },
            });
        }
        case 'transfer_out': {
            assertQuantity(movement.quantity, 'Transfer quantity');
            const o = outflow(p, movement.quantity, false);
            const shipment = {
                ownership: p.ownership,
                quantity: movement.quantity,
                value: money(owned ? o.value : 0n, ccy),
            };
            return result(o.position, -movement.quantity, -shipment.value.minor, { shipment });
        }
        case 'transfer_in': {
            const s = movement.shipment;
            if (s.ownership !== p.ownership) {
                throw new StockError('OWNERSHIP_MISMATCH', 'Receiving position has a different ownership from the shipment');
            }
            // Checked before it is used as a divisor. An unvalidated dispatch quantity reached `portion`
            // as a `BigInt` conversion (a bare `RangeError` for 2.5) or made the over-receipt message
            // read "Received 0 against -1 dispatched".
            assertQuantity(s.quantity, 'Dispatched quantity');
            assertCost(p, s.value, 'Shipment value');
            // A shipment is persisted like a position, so `INV-003` has to hold for it too. A memo
            // shipment carrying value used to hand back a `discrepancy.heldValue` on stock the shop does
            // not own, while `inTransitValue` excluded the same shipment by its ownership.
            if (!owned && s.value.minor !== 0n) {
                throw new StockError('POSITION_INCONSISTENT', `A ${s.ownership} shipment cannot carry inventory value (INV-003)`);
            }
            const got = movement.receivedQuantity;
            if (!Number.isSafeInteger(got) || got < 0) {
                throw new StockError('INVALID_QUANTITY', `Received quantity must be zero or more, got ${got}`);
            }
            if (got > s.quantity) {
                throw new StockError('TRANSFER_OVER_RECEIPT', `Received ${got} against ${s.quantity} dispatched`);
            }
            const receivedValue = got === 0 ? 0n : portion(s.value.minor, got, s.quantity);
            const discrepancy = got < s.quantity
                ? {
                    discrepancy: {
                        missingQuantity: s.quantity - got,
                        heldValue: money(s.value.minor - receivedValue, ccy),
                    },
                }
                : {};
            if (got === 0)
                return result(p, 0, 0n, discrepancy);
            if (!owned)
                return result({ ...p, quantity: p.quantity + got }, got, 0n, discrepancy);
            const r = inflow(p, got, receivedValue, method);
            return result(r.position, got, r.valueAdded, {
                ...discrepancy,
                costTrueUp: money(r.trueUp, ccy),
            });
        }
        case 'adjustment': {
            if (!Number.isSafeInteger(movement.delta) || movement.delta === 0) {
                throw new StockError('INVALID_QUANTITY', 'An adjustment needs a non-zero whole-number delta');
            }
            if (movement.delta > 0) {
                assertReason(movement.reason, ['count_variance']);
                return addFound(p, movement.delta, movement.cost, method);
            }
            assertReason(movement.reason, COUNT_REASONS);
            return removeLost(p, -movement.delta, false);
        }
        case 'write_off': {
            assertQuantity(movement.quantity, 'Write-off quantity');
            assertReason(movement.reason, LOSS_REASONS);
            return removeLost(p, movement.quantity, false);
        }
        case 'supplier_return': {
            assertQuantity(movement.quantity, 'Return-to-vendor quantity');
            return removeLost(p, movement.quantity, false);
        }
        case 'stocktake_correction': {
            // The same split as `adjustment`: stock that turns up was never destroyed by damage or lost
            // to shrinkage, so a surplus is a count variance and nothing else. Routing both signs
            // through `COUNT_REASONS` let a posted correction book found stock as `damage`, which
            // `adjustment` refuses for the same delta.
            assertReason(movement.reason, movement.delta > 0 ? ['count_variance'] : COUNT_REASONS);
            if (!Number.isSafeInteger(movement.delta) || movement.delta === 0) {
                throw new StockError('NO_VARIANCE', 'There is nothing to post for a line with no variance');
            }
            if (movement.approvalRequired && !movement.approvedBy) {
                throw new StockError('APPROVAL_REQUIRED', 'Variance above the threshold needs manager approval (INV-005, §15.17 step 5)');
            }
            return movement.delta > 0
                ? addFound(p, movement.delta, movement.surplusCost, method)
                : removeLost(p, -movement.delta, true);
        }
    }
}
function removeLost(p, qty, allowNegative) {
    const o = outflow(p, qty, allowNegative);
    const owned = p.ownership === 'owned';
    return result(o.position, -qty, owned ? -o.value : 0n, {
        ...(owned ? { cost: money(o.value, p.currency) } : {}),
        uncostedQuantity: o.uncosted,
    });
}
function addFound(p, qty, cost, method) {
    if (p.ownership !== 'owned')
        return result({ ...p, quantity: p.quantity + qty }, qty, 0n);
    let total;
    if (cost) {
        assertCost(p, cost, 'Found-stock cost');
        total = cost.minor;
    }
    else if (p.quantity > 0) {
        // Surplus found in a populated pool is valued at that pool's current average cost.
        total = portion(positionValue(p).minor, qty, p.quantity);
    }
    else {
        throw new StockError('COST_REQUIRED', 'Found stock in an empty position needs a cost; the spec gives no fallback');
    }
    const r = inflow(p, qty, total, method);
    return result(r.position, qty, r.valueAdded, { costTrueUp: money(r.trueUp, p.currency) });
}
/** Value of goods on the road. §12 case 46: it stays owned stock, it does not vanish while in transit. */
export function inTransitValue(shipments, currency) {
    return shipments.reduce((sum, s) => (s.ownership === 'owned' ? add(sum, s.value) : sum), zero(currency));
}
/** Appendix Z Stock valuation: "owned vs consignment", "consignment excluded from owned" (`INV-003`). */
export function valuate(positions, currency) {
    let ownedValue = zero(currency);
    let ownedQuantity = 0;
    let consignmentQuantity = 0;
    for (const p of positions) {
        requireKnownOwnership(p);
        assertConsistent(p);
        if (p.currency !== currency) {
            throw new StockError('CURRENCY_MISMATCH', `${p.currency} position in a ${currency} valuation`);
        }
        if (p.ownership === 'owned') {
            ownedValue = add(ownedValue, positionValue(p));
            ownedQuantity += Math.max(p.quantity, 0);
        }
        else {
            consignmentQuantity += p.quantity;
        }
    }
    return { ownedValue, ownedQuantity, consignmentQuantity };
}
/**
 * `INV-002`: "changing it requires an explicit revaluation event", and §12 case 51 lists a change
 * mid-year without saying what happens to value. This keeps total value as it is (layers merge
 * going to weighted average; a single averaged layer is all there is going to FIFO) and records
 * the event, so before equals after. If the accountant wants value to move, that is a separate
 * priced adjustment the spec does not define.
 */
export function changeCostingMethod(position, from, to) {
    if (from === to)
        throw new StockError('METHOD_UNCHANGED', `Method is already ${from}`);
    // The same three guards every movement runs. Without them an `on_loan` position, whose valuation
    // the spec never states, could be revalued, and `from` could name a method the layers contradict.
    requireKnownOwnership(position);
    assertConsistent(position);
    assertMethodFits(position, from);
    const layers = to === 'weighted_average' ? mergeLayers(position.layers, position.currency) : position.layers;
    const next = { ...position, layers };
    return {
        position: next,
        event: {
            reason: 'revaluation',
            from,
            to,
            valueBefore: positionValue(position),
            valueAfter: positionValue(next),
        },
    };
}
/** §15.17 step 2: counting is blind, so the sheet the counter sees has no expected quantity. */
export function blindCountSheet(lines) {
    return lines.map((l) => ({ itemKey: l.itemKey, ownership: l.snapshot.ownership }));
}
/** §15.17 step 4: expected, counted, difference, value. */
export function stocktakeVariance(lines, method, currency) {
    const out = [];
    const uncounted = [];
    let net = 0n;
    let abs = 0n;
    let consignment = 0;
    for (const l of lines) {
        requireKnownOwnership(l.snapshot);
        assertConsistent(l.snapshot, `Snapshot for ${l.itemKey}`);
        assertMethodFits(l.snapshot, method);
        if (l.snapshot.currency !== currency) {
            throw new StockError('CURRENCY_MISMATCH', `${l.itemKey} is not ${currency}`);
        }
        if (l.counted === undefined) {
            uncounted.push(l.itemKey);
            continue;
        }
        if (!Number.isSafeInteger(l.counted) || l.counted < 0) {
            throw new StockError('INVALID_QUANTITY', `Counted quantity for ${l.itemKey} must be zero or more`);
        }
        const expected = l.snapshot.quantity;
        const difference = l.counted - expected;
        let value = 0n;
        if (l.snapshot.ownership === 'owned' && difference !== 0) {
            if (difference < 0) {
                const available = Math.max(expected, 0);
                value = -takeLayers(l.snapshot.layers, Math.min(-difference, available), currency).value;
            }
            else if (l.surplusCost) {
                assertCost(l.snapshot, l.surplusCost, 'Surplus cost');
                value = l.surplusCost.minor;
            }
            else if (expected > 0) {
                value = portion(positionValue(l.snapshot).minor, difference, expected);
            }
            else {
                throw new StockError('COST_REQUIRED', `${l.itemKey} has a surplus and no cost basis to value it`);
            }
        }
        if (l.snapshot.ownership === 'consignment')
            consignment += difference;
        net += value;
        abs += value < 0n ? -value : value;
        out.push({
            itemKey: l.itemKey,
            ownership: l.snapshot.ownership,
            expected,
            counted: l.counted,
            difference,
            value: money(value, currency),
        });
    }
    return {
        lines: out,
        uncounted,
        netOwnedValue: money(net, currency),
        absoluteOwnedValue: money(abs, currency),
        consignmentDifference: consignment,
    };
}
/**
 * §15.17 step 5 and Appendix I: "Above tenant threshold ... B without approval". The threshold,
 * its unit and whether it applies per line or to the whole count are all unspecified. It is taken
 * as a parameter, compared to the absolute owned value across the count, and not defaulted.
 *
 * Unset fails closed. `POS-008a` calls an unset limit "inert", but for a gate meant to stop
 * unreviewed write-downs, inert-means-allow would let every variance post unapproved until
 * someone found the setting. So unset means any non-zero variance needs approval. A consignment
 * variance has no owned value to compare, so it always needs approval: the loss is the
 * supplier's and the shop cannot size it.
 */
export function stocktakeApproval(report, threshold) {
    if (!report.lines.some((l) => l.difference !== 0))
        return { required: false, why: 'no_variance' };
    if (report.lines.some((l) => l.ownership === 'consignment' && l.difference !== 0)) {
        return { required: true, why: 'consignment_variance' };
    }
    if (!threshold)
        return { required: true, why: 'threshold_unset' };
    if (threshold.currency !== report.absoluteOwnedValue.currency) {
        throw new StockError('CURRENCY_MISMATCH', 'Threshold currency differs from the count');
    }
    return report.absoluteOwnedValue.minor > threshold.minor
        ? { required: true, why: 'above_threshold' }
        : { required: false, why: 'within_threshold' };
}
/** Partial counts may be saved (§15.17 step 3) but only a complete count may post. */
export function stocktakeCorrections(report) {
    if (report.uncounted.length > 0) {
        throw new StockError('STOCKTAKE_INCOMPLETE', `Not yet counted: ${report.uncounted.join(', ')}`);
    }
    return report.lines
        .filter((l) => l.difference !== 0)
        .map((l) => ({ itemKey: l.itemKey, delta: l.difference }));
}
/**
 * `INV-006`: "over-receipt tolerance" with no value. Taken as basis points of the ordered quantity
 * so it stays integer. Absent means zero tolerance, the strictest reading; an over-receipt is then
 * reported as `over_tolerance` for a person to decide, not silently accepted.
 */
export function checkReceipt(input) {
    assertQuantity(input.ordered, 'Ordered quantity');
    assertQuantity(input.receiving, 'Receiving quantity');
    if (!Number.isSafeInteger(input.alreadyReceived) || input.alreadyReceived < 0) {
        throw new StockError('INVALID_QUANTITY', 'Already received must be zero or more');
    }
    assertThreshold(input.toleranceBps, 'Over-receipt tolerance');
    const total = input.alreadyReceived + input.receiving;
    if (total <= input.ordered)
        return { outcome: 'within_order', backOrder: input.ordered - total };
    const overBy = total - input.ordered;
    const allowed = Math.floor((input.ordered * (input.toleranceBps ?? 0)) / 10000);
    return overBy <= allowed
        ? { outcome: 'over_within_tolerance', overBy, backOrder: 0 }
        : { outcome: 'over_tolerance', overBy, backOrder: 0 };
}
// ---------------------------------------------------------------------------------------------
// Expiry, INV-009
// ---------------------------------------------------------------------------------------------
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * The round-trip checks all three fields, not just the day.
 *
 * `Date.UTC` normalises an out-of-range month instead of refusing it, and maps a year under 100
 * into the 1900s, so checking only `getUTCDate()` let `2026-13-01` through as 2027-01-01 and
 * `0099-01-01` through as 1999-01-01. An expiry date of `2026-13-01` was therefore sellable all
 * through 2026, which is the one thing `INV-009` is a hard block against.
 */
function dayNumber(date) {
    if (!ISO_DATE.test(date))
        throw new StockError('INVALID_DATE', `Expected YYYY-MM-DD, got ${JSON.stringify(date)}`);
    const [y, m, d] = date.split('-').map(Number);
    const t = Date.UTC(y, m - 1, d);
    const back = new Date(t);
    if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) {
        throw new StockError('INVALID_DATE', `${date} is not a calendar date`);
    }
    return Math.round(t / 86_400_000);
}
export function daysBetween(from, to) {
    return dayNumber(to) - dayNumber(from);
}
/** The expiry date is the last good day. §12 case 48: hard block, override audited. */
export function assertSellableByExpiry(expiryDate, today, override) {
    if (daysBetween(expiryDate, today) <= 0)
        return { overridden: false };
    // Both fields, not just the reason. An override record with no approver is not an audited
    // override, and §12 case 48 allows the sale only because the override is audited.
    if (override && override.reason.trim() !== '' && override.approvedBy.trim() !== '') {
        return { overridden: true, override };
    }
    throw new StockError('EXPIRED_STOCK', `Lot expired on ${expiryDate}; selling it needs an audited override`);
}
/** `INV-009`: the near-expiry window is unspecified, so it is a required argument, not a default. */
export function nearExpiry(lots, today, withinDays) {
    if (!Number.isSafeInteger(withinDays) || withinDays < 0) {
        throw new StockError('INVALID_THRESHOLD', 'Near-expiry window must be zero or more days');
    }
    return lots.filter((l) => {
        const left = daysBetween(today, l.expiryDate);
        return left >= 0 && left <= withinDays;
    });
}
// ---------------------------------------------------------------------------------------------
// Ageing and dead stock, INV-010
// ---------------------------------------------------------------------------------------------
/** Appendix Z Dead stock: "default 180 days no sale". The only threshold in this module the spec gives. */
export const DEAD_STOCK_DEFAULT_DAYS = 180;
export function stockAgeing(input) {
    assertThreshold(input.deadAfterDays, 'Dead-stock threshold');
    const daysOnHand = daysBetween(input.receivedOn, input.today);
    const since = input.lastSoldOn ? daysBetween(input.lastSoldOn, input.today) : undefined;
    const idle = since ?? daysOnHand;
    const limit = input.deadAfterDays ?? DEAD_STOCK_DEFAULT_DAYS;
    return {
        daysOnHand,
        ...(since !== undefined ? { daysSinceSale: since } : {}),
        dead: idle > limit,
    };
}
/** `INV-010`: how a markdown is suggested is unspecified, so the tenant supplies the schedule. No schedule, no suggestion. */
export function suggestMarkdownBps(daysOnHand, schedule) {
    let best = 0;
    // -1 is below every step the validation below admits, so a step at day zero still wins. Before
    // that validation a step of `afterDays: -1` tied the sentinel and was dropped without a word.
    let bestAfter = -1;
    for (const s of schedule) {
        assertThreshold(s.afterDays, 'Markdown step day');
        assertThreshold(s.markdownBps, 'Markdown basis points');
        if (daysOnHand > s.afterDays && s.afterDays > bestAfter) {
            best = s.markdownBps;
            bestAfter = s.afterDays;
        }
    }
    return best;
}
/** Integer arithmetic only: demand over a window is `soldInPeriod * days / periodDays`, rounded up. */
export function suggestReorder(i) {
    for (const [name, v] of Object.entries({
        soldInPeriod: i.soldInPeriod,
        leadTimeDays: i.leadTimeDays,
        coverDays: i.coverDays,
        safetyStock: i.safetyStock,
        onOrder: i.onOrder,
    })) {
        if (!Number.isSafeInteger(v) || v < 0)
            throw new StockError('INVALID_QUANTITY', `${name} must be zero or more`);
    }
    // `onHand` is checked apart from the rest because it is the one quantity that may be negative
    // (§12 case 47), which is why it was left out of the loop and so went unchecked altogether: an
    // `onHand` of 1.5 produced a suggested purchase order of 21.5 units, and `NaN` produced `NaN`.
    if (!Number.isSafeInteger(i.onHand)) {
        throw new StockError('INVALID_QUANTITY', 'onHand must be a whole number');
    }
    if (!Number.isSafeInteger(i.periodDays) || i.periodDays <= 0) {
        throw new StockError('INVALID_THRESHOLD', 'Sell-through period must be a positive number of days');
    }
    const demand = (days) => Math.ceil((i.soldInPeriod * days) / i.periodDays);
    const reorderPoint = demand(i.leadTimeDays) + i.safetyStock;
    const position = Math.max(i.onHand, 0) + i.onOrder;
    if (position > reorderPoint)
        return { reorderPoint, suggestedQuantity: 0 };
    const target = demand(i.leadTimeDays + i.coverDays) + i.safetyStock;
    return { reorderPoint, suggestedQuantity: Math.max(target - position, 0) };
}
/**
 * "The nearest available blanks" has no metric in the spec. This ranks by the sum of the absolute
 * sphere and cylinder differences in quarter-dioptre steps, in-stock only, ties by id. Whether a
 * stronger or weaker blank should win is an optical judgement and is not made here.
 * `maxDistanceSteps` is optional; absent returns every in-stock blank, ranked.
 */
export function nearestBlanks(blanks, want, maxDistanceSteps) {
    const steps = (a, b) => Math.abs(Math.round((toNumber(a) - toNumber(b)) * 4));
    return blanks
        .filter((b) => b.quantity > 0)
        .map((blank) => ({
        blank,
        distanceSteps: steps(blank.sphere, want.sphere) + steps(blank.cylinder, want.cylinder),
    }))
        .filter((m) => maxDistanceSteps === undefined || m.distanceSteps <= maxDistanceSteps)
        .sort((a, b) => a.distanceSteps - b.distanceSteps || (a.blank.id < b.blank.id ? -1 : 1));
}
//# sourceMappingURL=stock.js.map